// Angular modules
import { Injectable } from '@angular/core';

// Internal modules
import { environment } from '@env/environment';

// Enums
import { StorageKey } from '@enums/storage-key.enum';
import { LoginTokenResponse, BaseTokenResponse } from '../../dto/authentication/token-response.dto';
import { UserInfo } from '../../dto/account-user/account-user.dto';

@Injectable()
export class StorageHelper {
  private static storagePrefix: string = environment.appName + '_' + environment.version + '_';

  // NOTE Token Section
  public static setToken(token: LoginTokenResponse | BaseTokenResponse): void {
    StorageHelper.setItem(StorageKey.TOKEN, token);
  }

  public static removeToken(): void {
    StorageHelper.removeItem(StorageKey.TOKEN);
  }

  public static setUser(user: UserInfo): void {
    StorageHelper.setItem(StorageKey.USER, user);
  }

  public static getUser(): UserInfo | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const user = StorageHelper.getItem(StorageKey.USER);
      return user && typeof user.id === 'string' && typeof user.name === 'string'
        ? user as UserInfo
        : null;
    } catch {
      return null;
    }
  }

  public static removeUser(): void {
    if (typeof localStorage !== 'undefined') {
      StorageHelper.removeItem(StorageKey.USER);
    }
  }

  public static setRoles(roles: string[]): void {
    StorageHelper.setItem(StorageKey.ROLES, roles);
  }

  public static getRoles(): string[] | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const roles = StorageHelper.getItem(StorageKey.ROLES);
      return Array.isArray(roles) && roles.every(role => typeof role === 'string')
        ? roles as string[]
        : null;
    } catch {
      return null;
    }
  }

  public static removeRoles(): void {
    if (typeof localStorage !== 'undefined') {
      StorageHelper.removeItem(StorageKey.ROLES);
    }
  }

  public static getToken(): BaseTokenResponse | null {
    if (typeof localStorage === 'undefined') return null;

    const raw = StorageHelper.getItem(StorageKey.TOKEN);
    if (!raw) return null;

    try 
    {
      // raw might already be an object or a JSON string
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;

      // support both flat and { data: ... } envelope
      const payload = obj && 'data' in obj && obj.accessToken === undefined ? (obj as any).data : obj;
      return isTokenResponse(payload) ? (payload as BaseTokenResponse) : null;
    } 
    catch {
      return null;
    }
  }

  // ----------------------------------------------------------------------------------------------
  // SECTION LocalStorage -------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  public static setItem(key: string, value: any, prefix: boolean = true): void {
    const itemKey = this.prefixer(key, prefix);
    localStorage.setItem(itemKey, JSON.stringify(value));
  }

  public static getItem(key: string, prefix: boolean = true): any {
    const itemKey = this.prefixer(key, prefix);
    const res = localStorage.getItem(itemKey);
    if (res !== 'undefined')
      return JSON.parse(res as any);
    console.error('StorageHelper : getItem -> undefined key');
    return null;
  }

  public static removeItem(key: string, prefix: boolean = true): void {
    const itemKey = this.prefixer(key, prefix);
    localStorage.removeItem(itemKey);
  }

  public static getKeys(all: boolean = false): string[] {
    const keys: string[] = [];
    // NOTE Keys
    for (const key in localStorage)
      keys.push(key);
    if (all)
      return keys;
    // NOTE Prefixed keys
    return keys.filter((item) => item.startsWith(this.storagePrefix));
  }

  public static clearItems(all: boolean = false): void {
    // NOTE Keys
    if (all) {
      localStorage.clear();
      return;
    }
    // NOTE Prefixed keys
    const prefixedKeys = this.getKeys();
    for (const prefixedKey of prefixedKeys)
      this.removeItem(prefixedKey, false);
  }

  public static clearItemsWithoutCurrentPrefix(): void {
    const allKeys = this.getKeys(true);
    for (const key of allKeys)
      if (!key.startsWith(this.storagePrefix))
        this.removeItem(key, false);
  }

  // !SECTION LocalStorage

  // NOTE Private

  private static prefixer(key: string, autoPrefix: boolean): string {
    let itemKey = key;
    if (autoPrefix)
      itemKey = this.storagePrefix + key;
    return itemKey;
  }

}
function isTokenResponse(x: any): x is BaseTokenResponse {
  return x && typeof x.accessToken === 'string' && typeof x.refreshToken === 'string';
}


